/**
 * @module languages
 */

/**
 * @typedef {object} MgnlEnvVars
 * @property {string} REACT_APP_MGNL_APP_BASE - Magnolia app base path (e.g. '/').
 * @property {string} REACT_APP_MGNL_LANGUAGES - Space-separated list of language codes (e.g. 'en es fr it').
 */

/**
 * Convenience function to return a space-separated list of languages, based on the provided ENV variables.
 *
 * @param {MgnlEnvVars} mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 *
 * @returns {Array} Array of language codes (e.g. ['en', 'es', 'fr', 'it']).
 */
function getLanguages(mgnlEnvVars) {
  const envVars = mgnlEnvVars || process.env;
  return envVars.REACT_APP_MGNL_LANGUAGES.split(' ');
}

/**
 * Convenience function to get the current language from window location pathname.
 *
 * @param {MgnlEnvVars} mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 *
 * @returns {string} - The language (code) found in the pathname that matches one of the specified languages. If no language is found in the pathname the first of the specified languages is returned.
 */
function getCurrentLanguage(mgnlEnvVars) {
  const envVars = mgnlEnvVars || process.env;
  const languages = getLanguages(envVars);

  /* istanbul ignore next */
  if (!window || !window.location) {
    return languages[0];
  }

  const languageFromPathname = languages.find((language) =>
    new RegExp(`/${language}($|/)`).test(window.location.pathname),
  );

  return languageFromPathname || languages[0];
}

/**
 * Convenience function to remove the current language from the specified string.
 *
 * @param {string} string - The target string on which to remove the current language.
 * @param {string} currentLanguage - The current language value (e.g. 'en').
 *
 * @returns {string} The specified string, with current language instances replaced.
 */
function removeCurrentLanguage(string, currentLanguage) {
  return string.replace(new RegExp(`/${currentLanguage}($|/)`), '/');
}

/**
 * Convenience function to change the language and update the window location.
 *
 * @param {string} newLanguage - The new language value.
 * @param {MgnlEnvVars} mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 */
function changeLanguage(newLanguage, mgnlEnvVars) {
  const envVars = mgnlEnvVars || process.env;
  const nodeName = envVars.REACT_APP_MGNL_APP_BASE || '/';
  const languages = getLanguages(envVars);
  let { pathname } = window.location;
  const currentLanguage = getCurrentLanguage(envVars);
  pathname = removeCurrentLanguage(pathname, currentLanguage);

  if (languages[0] !== newLanguage) {
    if (pathname.indexOf(nodeName) > -1) {
      pathname = pathname.replace(
        new RegExp(nodeName),
        `/${newLanguage}${nodeName}`,
      );
    } else {
      pathname = `/${newLanguage}${pathname}`;
    }
  }

  window.location.href =
    window.location.origin + pathname + window.location.search;
}

module.exports = {
  changeLanguage,
  getCurrentLanguage,
  getLanguages,
  removeCurrentLanguage,
};
